import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * 全局访问门:未解锁时以蒙版盖住整个后台,输入正确口令后放行。
 *
 * 性质说明(重要):这是**前端遮挡**,不是安全边界 —— 口令校验在浏览器里跑,
 * 打包产物可被查看,接口也仍可被直接调用。它挡的是「随手点开/旁人误入/演示时被翻页」,
 * 不能用来保护敏感数据。真要鉴权须走服务端(登录态 + 接口校验)。
 *
 * 口令不写明文:存 djb2 摘要,避免在 bundle 里被直接 grep 出来(仅提高一点门槛)。
 */
const PASSCODE_DIGEST = "54387dfd";
const STORAGE_KEY = "admin.gate.unlocked";

function digest(input: string): string {
	// djb2:短、稳定、无依赖;仅用于避免明文,不作密码学用途
	let h = 5381;
	for (let i = 0; i < input.length; i += 1) {
		h = ((h << 5) + h + input.charCodeAt(i)) >>> 0;
	}
	return h.toString(16).padStart(8, "0");
}

function readUnlocked(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEY) === PASSCODE_DIGEST;
	} catch {
		return false; // 隐私模式/禁用存储 → 每次都要输,不炸
	}
}

export function PasswordGate({ children }: { children: React.ReactNode }) {
	const [unlocked, setUnlocked] = useState<boolean>(readUnlocked);
	const [value, setValue] = useState("");
	const [error, setError] = useState(false);

	// 多标签页同步:一处解锁/登出,其余标签页跟随
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === STORAGE_KEY) setUnlocked(readUnlocked());
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	if (unlocked) return <>{children}</>;

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		if (digest(value) !== PASSCODE_DIGEST) {
			setError(true);
			setValue("");
			return;
		}
		try {
			localStorage.setItem(STORAGE_KEY, PASSCODE_DIGEST);
		} catch {
			// 存不下也放行本次会话
		}
		setUnlocked(true);
	};

	return (
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center bg-surface-background px-4"
			data-testid="admin-password-gate"
		>
			<form
				onSubmit={submit}
				className="w-full max-w-[360px] rounded-2xl border border-surface-border bg-surface-card p-6 shadow-sm"
			>
				<h1 className="text-base font-medium text-text-primary">管理后台</h1>
				<p className="mt-1 text-sm text-text-secondary">请输入访问口令</p>
				<Input
					type="password"
					value={value}
					autoFocus
					aria-label="访问口令"
					aria-invalid={error}
					placeholder="访问口令"
					onChange={(e) => {
						setValue(e.target.value);
						if (error) setError(false);
					}}
					className="mt-4"
					data-testid="admin-password-input"
				/>
				{error ? (
					<p className="mt-2 text-sm text-status-danger" role="alert">
						口令不正确
					</p>
				) : null}
				<Button
					type="submit"
					className="mt-4 w-full"
					disabled={!value}
					data-testid="admin-password-submit"
				>
					进入
				</Button>
			</form>
		</div>
	);
}

/** 退出登录(清除解锁态);供需要「锁定」入口的页面调用。 */
export function lockAdmin(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}
